<template>
  <div>
    <drawer-section title="Details" icon="icon-tag">
      <base-input label="Name" placeholder="Category name" required v-model="name" :error="fieldError('name')" />
      <base-textarea label="Description" :rows="2" placeholder="Description (optional)" v-model="description" />
      <label class="d-block mt-3 mb-2">Color</label>
      <div class="swatch-picker">
        <button v-for="preset in COLOR_PRESETS" :key="preset" type="button"
                class="swatch-picker__swatch"
                :class="{ 'swatch-picker__swatch--active': color === preset }"
                :style="{ backgroundColor: preset }"
                :aria-label="'Color ' + preset"
                @click="color = preset"></button>
        <ColorPicker v-model="color" show-alpha size="small" class="swatch-picker__more" />
      </div>
    </drawer-section>
    <drawer-section title="Location check" icon="icon-pin">
      <info-hint v-if="!pinReady && status !== 'checking'" variant="warning" icon="icon-alert-circle-exc" class="mb-3">
        {{ pinHint }}
        <template #action>
          <base-button variant="outlined" tone="primary" size="sm" @click="goToGroupSettings">Open Group settings</base-button>
        </template>
      </info-hint>
      <label class="d-block">When someone starts a timer off site</label>
      <segmented-toggle v-model="mode" :options="modeOptions" size="sm" />
      <small class="text-white opacity-7 d-block mt-2">{{ modeHint }}</small>
      <info-hint v-if="mode === 'warning' && !offSiteAutomationActive" variant="primary" icon="icon-bell-55" class="mt-3">
        Get an email when someone starts this category off site.
        <template #action>
          <base-button variant="outlined" tone="primary" size="sm" @click="goToAutomation">Create automation</base-button>
        </template>
      </info-hint>
      <template v-if="mode !== 'off'">
        <base-input class="form-field--compact mt-3 mb-1" :label="`Allowed distance (${unit})`" :error="fieldError('location_radius_m')">
          <!-- No :min / :max: el-input-number would clamp silently; submit() shows the range error instead. -->
          <el-input-number v-model="distance" size="small" :controls="false" :precision="0" />
        </base-input>
        <small class="text-white opacity-7 d-block">{{ rangeText }}</small>
        <location-map v-if="pinLat !== null && pinLng !== null"
                      class="mt-3"
                      :latitude="pinLat" :longitude="pinLng" :radius-m="previewRadiusM"
                      :interactive="false" height="180px" />
      </template>
      <small v-if="fieldError('location_check_mode')" class="text-danger d-block mt-2">{{ fieldError('location_check_mode') }}</small>
    </drawer-section>
  </div>
</template>

<script setup lang="ts">
// Time tracking categories drawer, edit view (OTC-2926): name, color,
// description and location check save together through the category create
// and update lambdas. Distances are stored in meters and entered in feet for
// imperial groups.
import { ref, computed, onMounted, defineAsyncComponent, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
// Imported by source name so babel-plugin-component resolves the matching
// CSS as element-ui/lib/theme-chalk/color-picker.css. Aliasing the import
// (e.g. `ColorPicker as ElColorPicker`) makes the plugin look for
// el-color-picker.css, which doesn't exist. Element UI's ColorPicker is
// not globally registered (see src/plugins/globalComponents.js); the
// template tag below uses <ColorPicker> so script-setup binds it.
import { ColorPicker } from 'element-ui'
import { API_LOCATION } from '@/api-config'
import { COLOR_PRESETS } from '@/util/colorPresets'
import {
  RADIUS_DEFAULT_M,
  RADIUS_MIN_M,
  RADIUS_MAX_M,
  LOCATION_MODE_OPTIONS,
  metersToInputUnits,
  inputUnitsToMeters,
  distanceUnitLabel,
  groupCountry,
} from '@/util/locationCheck'

type Category = {
  id: number
  name: string
  label_color?: string
  description?: string | null
  location_check_mode?: string
  location_radius_m?: number | null
}

const LocationMap = defineAsyncComponent(() => import(/* webpackChunkName: "maplibre" */ '@/components/LocationMap.vue'))

// Runtime prop declarations: a TS-only `Category | null` would compile to a
// literal null in the type array, which Vue 2.7 rejects.
const props = defineProps({
  category: { type: Object as PropType<Category | null>, default: null },
  companyId: { type: String, required: true },
  companyGroupId: { type: [String, Number], required: true },
  venuePinSet: { type: Boolean, default: false },
  offSiteAutomationActive: { type: Boolean, default: false },
  serverErrors: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
})

const emit = defineEmits(['save'])

const country = groupCountry()
const unit = distanceUnitLabel(country)
const minUnits = metersToInputUnits(RADIUS_MIN_M, country)
const maxUnits = metersToInputUnits(RADIUS_MAX_M, country)
const rangeText = `${minUnits.toLocaleString()} to ${maxUnits.toLocaleString()} ${unit}`

const name = ref(props.category?.name || '')
const color = ref(props.category?.label_color || '#42b883')
const description = ref(props.category?.description || '')
const mode = ref(props.category?.location_check_mode || 'off')
const distance = ref(metersToInputUnits(Number(props.category?.location_radius_m) || RADIUS_DEFAULT_M, country))
const errors = ref<Record<string, string>>({})

// Venue pin: '' until checked. The GET also fills the pin from the address
// when the group has one, so a group without a saved pin can still turn checks on.
const status = ref('')
const pinLat = ref<number | null>(null)
const pinLng = ref<number | null>(null)
const pinReady = computed(() => props.venuePinSet || status.value === 'resolved')
const pinHint = computed(() => status.value === 'unresolved'
  ? "Your venue address couldn't be located. Set the venue pin in Group settings to use location checks."
  : 'Add your venue address in Group settings to use location checks.')

const modeOptions = computed(() => LOCATION_MODE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
  disabled: o.value !== 'off' && !pinReady.value,
})))
const modeHint = computed(() => (LOCATION_MODE_OPTIONS.find((o) => o.value === mode.value) || LOCATION_MODE_OPTIONS[0]).description)

const previewRadiusM = computed(() =>
  Math.min(RADIUS_MAX_M, Math.max(RADIUS_MIN_M, inputUnitsToMeters(Number(distance.value) || 0, country))))

function fieldError(key: string): string {
  return errors.value[key] || props.serverErrors[key] || ''
}

async function loadPin() {
  status.value = 'checking'
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const response = await axios.get(
      `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/location`,
      { headers: { Authorization: token } }
    )
    const data = response.data || {}
    status.value = data.status || 'lookup_failed'
    if (data.latitude !== null && data.latitude !== undefined && data.longitude !== null && data.longitude !== undefined) {
      pinLat.value = Number(data.latitude)
      pinLng.value = Number(data.longitude)
    }
  } catch (err) {
    status.value = 'lookup_failed'
  }
}

onMounted(loadPin)

// New tab, so the category being edited stays open.
function goToAutomation() {
  window.open('/mail/custom/automation', '_blank', 'noopener')
}

function goToGroupSettings() {
  window.location.href = '/settings/group?id=' + props.companyGroupId + '&tab=0'
}

function submit() {
  errors.value = {}
  const next: Record<string, string> = {}
  if (!name.value.trim()) next.name = 'Category name cannot be empty'
  const d = Number(distance.value)
  if (mode.value !== 'off' && (!Number.isFinite(d) || d < minUnits || d > maxUnits)) {
    next.location_radius_m = `Allowed distance must be between ${minUnits.toLocaleString()} and ${maxUnits.toLocaleString()} ${unit}`
  }
  if (Object.keys(next).length) {
    errors.value = next
    return
  }
  emit('save', {
    name: name.value.trim(),
    description: description.value,
    label_color: color.value,
    location_check_mode: mode.value,
    location_radius_m: mode.value === 'off' ? null : inputUnitsToMeters(d, country),
  })
}

defineExpose({ submit })
</script>
